import { updateState } from '@angular-architects/ngrx-toolkit';
import { signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { environment } from '@dxp/core/environment';
import { IRbacChannelProviderConfiguration } from '@dxp/core/models';
import { inject, DestroyRef } from '@angular/core';
import { RbacApiService } from '@dxp/core/api';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
    filter,
    map,
    distinctUntilChanged,
    switchMap,
    catchError,
    forkJoin,
    of,
    tap,
} from 'rxjs';
import { ChannelProviderType, ChannelProviderTypes } from '@dxp/core/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface IChannelProviderStore {
    channelProviders: Record<
        ChannelProviderType,
        IRbacChannelProviderConfiguration[]
    >;
}

function createEmptyChannelProvidersRecord(): Record<
    ChannelProviderType,
    IRbacChannelProviderConfiguration[]
> {
    return ChannelProviderTypes.reduce(
        (acc, type) => {
            acc[type] = [];
            return acc;
        },
        {} as Record<ChannelProviderType, IRbacChannelProviderConfiguration[]>,
    );
}

const initialState: IChannelProviderStore = {
    channelProviders: createEmptyChannelProvidersRecord(),
};

export const ChannelProviderStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('ChannelProviderStore'),
    withState(initialState),
    withMethods(store => ({
        setChannelProviders(
            channelProviders: Record<
                ChannelProviderType,
                IRbacChannelProviderConfiguration[]
            >,
        ) {
            const next = ChannelProviderTypes.reduce(
                (acc, type) => {
                    acc[type] = [...(channelProviders[type] ?? [])];
                    return acc;
                },
                {} as Record<
                    ChannelProviderType,
                    IRbacChannelProviderConfiguration[]
                >,
            );

            updateState(store, 'UPDATE_CHANNEL_PROVIDERS', {
                channelProviders: next,
            });
        },
        getChannelProvidersByWorkflowId(
            workflowId: string,
        ): ChannelProviderType[] {
            const result: ChannelProviderType[] = [];

            for (const [type, configs] of Object.entries(
                store.channelProviders(),
            )) {
                if (configs.some(c => c.workflowId === workflowId)) {
                    result.push(type as ChannelProviderType);
                }
            }

            return result;
        },
    })),
    withHooks({
        onInit(
            store,
            oidcSecurityService = inject(OidcSecurityService),
            rbacApiService = inject(RbacApiService),
            destroyRef = inject(DestroyRef),
        ) {
            oidcSecurityService.isAuthenticated$
                .pipe(
                    map(result => result.isAuthenticated),
                    distinctUntilChanged(),
                    filter(Boolean),
                    switchMap(() => {
                        const requests = ChannelProviderTypes.map(type =>
                            rbacApiService
                                .getChannelProvidersConfiguration(type)
                                .pipe(
                                    catchError(() =>
                                        of(
                                            [] as IRbacChannelProviderConfiguration[],
                                        ),
                                    ),
                                ),
                        );

                        return forkJoin(requests).pipe(
                            map(results => {
                                return ChannelProviderTypes.reduce(
                                    (acc, type, index) => {
                                        acc[type] = results[index] ?? [];
                                        return acc;
                                    },
                                    createEmptyChannelProvidersRecord(),
                                );
                            }),
                            tap(record => store.setChannelProviders(record)),
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
